import { styled } from '@linaria/react';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { Fragment, memo, useContext } from 'react';
import { WorkflowStatus } from '../../../api/process-client/process-client.type';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { newCOLORS } from '../../../styles/colors';

import { resetProcessTaskDetails } from '../../process/process-graph/process-graph.helpers';
import { ProcessContext } from '../context/process.context';
import type { WorkflowNodeData } from '../reactflow.type';
import { getBackground } from './workflow-node.helpers';

const WorkflowNode = memo(({ data }: NodeProps) => {
  const nodeData = data as WorkflowNodeData;
  const isBackwards = nodeData.status === WorkflowStatus.Backwards;

  return (
    <Fragment>
      <WorkflowNodeComponent data={nodeData} />
      {isBackwards ? (
        <Fragment>
          <Handle type="source" position={Position.Top} id="top" />
          <Handle type="target" position={Position.Bottom} id="bottom" />
        </Fragment>
      ) : (
        <Fragment>
          <Handle type="target" position={Position.Top} id="top" />
          <Handle type="source" position={Position.Bottom} id="bottom" />
        </Fragment>
      )}
    </Fragment>
  );
});

const WorkflowNodeComponent = ({ data }: { data: WorkflowNodeData }) => {
  const processContext = useContext(ProcessContext);
  const processStore = useProcessStore();

  const { setWorkflow: setLeftWorkflow, setRightWorkflow } = processContext;
  const { setWorkflow: setLeftWorkflowStore, setRightWorkflow: setRightWorkflowStore } = processStore;
  const setWorkflow = data.isRightGraph ? setRightWorkflow : setLeftWorkflow;
  const setWorkflowStore = data.isRightGraph ? setRightWorkflowStore : setLeftWorkflowStore;
  return (
    <Container
      status={data.status}
      onClick={() => {
        resetProcessTaskDetails(processContext, processStore);

        const workflow = {
          source: data.sourceStage,
          destination: data.destinationStage,
          status: data.status,
          mapping: data.mapping,
        };

        setWorkflow(workflow);
        setWorkflowStore(workflow);
      }}
    >
      {data.value}
    </Container>
  );
};

export { WorkflowNode };

const Container = styled.div<{ status: WorkflowStatus }>`
  min-width: 60px;
  max-width: 120px;
  height: 30px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid ${newCOLORS.purple2};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ status }: { status: WorkflowStatus }) => getBackground(status)};
  color: ${({ status }: { status: WorkflowStatus }) =>
    status === WorkflowStatus.Backwards ? newCOLORS.purple2 : newCOLORS.white};
  text-align: center;
  font-family: Figtree;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
